import { useMemo, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import {
  formatIsoWeek,
  formatIsoWeekRange,
  isoWeekNumber,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"

export type UsageMcpWeekRow = {
  WeekStart: string
  Calls: number
  Errors: number
  Users: number
  Customers?: number | null
  NewCustomers?: number | null
  InternalCalls?: number | null
}

export type UsageMcpWeekChartProps = ComponentProps<"div"> & {
  /** ISO weeks, oldest first; the last one is usually the running week. */
  weeks: UsageMcpWeekRow[]
  /** Show the customer columns (portfolio) or only calls and people (customer). */
  showCustomers?: boolean
  /** Weeks shown in the table before "show all" (chart shows every week). */
  tableLimit?: number
}

type ChartPoint = {
  week: string
  label: string
  calls: number
  users: number
  customers: number
  partial: boolean
}

const CHART_CONFIG = {
  calls: { label: "Tool calls", color: DATA_VIZ.brand.green2 },
}

// Week by week, the way the report told it: tool calls per ISO week as
// bars, with the people (and customers) that week in the table below. The
// running week is drawn lighter and labelled "so far" — it is never a
// trend. Rendering only: the weeks come aggregated from SQL.
function UsageMcpWeekChart(props: UsageMcpWeekChartProps) {
  const {
    weeks,
    showCustomers = false,
    tableLimit = 12,
    className,
    ...divProps
  } = props

  const points = useMemo<ChartPoint[]>(() => {
    const thisMonday = new Date()
    thisMonday.setUTCHours(0, 0, 0, 0)
    thisMonday.setUTCDate(
      thisMonday.getUTCDate() - ((thisMonday.getUTCDay() + 6) % 7),
    )
    return weeks.map((week) => ({
      week: week.WeekStart,
      label: `W${isoWeekNumber(new Date(week.WeekStart))}`,
      calls: week.Calls,
      users: week.Users,
      customers: week.Customers ?? 0,
      partial: new Date(week.WeekStart).getTime() >= thisMonday.getTime(),
    }))
  }, [weeks])

  const tableRows = useMemo(
    () => [...weeks].reverse().slice(0, tableLimit),
    [weeks, tableLimit],
  )

  if (weeks.length === 0) {
    return (
      <p className={cn("text-xs text-muted-foreground", className)}>
        No tool calls in the window.
      </p>
    )
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} {...divProps}>
      <Chart config={CHART_CONFIG} className="h-[200px] w-full">
        <Chart.BarChart data={points} margin={{ left: -6, right: 4, top: 18 }}>
          <Chart.Grid vertical={false} />
          <Chart.Axis.X
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={6}
            className="text-xs text-muted-foreground"
          />
          <Chart.Axis.Y
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            width={60}
            allowDecimals={false}
            tickFormatter={(value: number) => formatNumber(value)}
            className="text-xs text-muted-foreground"
          />
          <Chart.Tooltip
            content={<UsageMcpWeekTooltip showCustomers={showCustomers} />}
          />
          <Chart.Bar
            dataKey="calls"
            radius={[2, 2, 0, 0]}
            maxBarSize={44}
            // A report renders complete on first paint (and in a screenshot
            // or a background tab, where animation frames never run).
            isAnimationActive={false}
            label={{
              position: "top",
              fontSize: 10,
              className: "fill-muted-foreground",
              formatter: (value: unknown) => formatNumber(Number(value)),
            }}
          >
            {points.map((point) => (
              <Chart.BarCell
                key={point.week}
                fill={DATA_VIZ.brand.green2}
                fillOpacity={point.partial ? 0.45 : 1}
              />
            ))}
          </Chart.Bar>
        </Chart.BarChart>
      </Chart>

      <div className="overflow-x-auto">
        <table className="usage-report-table">
          <thead>
            <tr>
              <th>Week</th>
              <th>Dates</th>
              <th className="num">Tool calls</th>
              <th className="num">People</th>
              {showCustomers && (
                <>
                  <th className="num">Customers</th>
                  <th className="num">New customers</th>
                  <th className="num">Internal calls</th>
                </>
              )}
              <th className="num">Failed</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((week, index) => {
              const partial = index === 0 && points[points.length - 1]?.partial
              return (
                <tr key={week.WeekStart}>
                  <td className="whitespace-nowrap font-medium">
                    {formatIsoWeek(week.WeekStart)}
                    {partial && (
                      <span className="ml-1 font-normal text-muted-foreground">
                        so far
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground">
                    {formatIsoWeekRange(week.WeekStart)}
                  </td>
                  <td className="num">{formatNumber(week.Calls)}</td>
                  <td className="num">{formatNumber(week.Users)}</td>
                  {showCustomers && (
                    <>
                      <td className="num">
                        {formatNumber(week.Customers ?? 0)}
                      </td>
                      <td className="num">
                        {week.NewCustomers
                          ? formatNumber(week.NewCustomers)
                          : "—"}
                      </td>
                      <td className="num text-muted-foreground">
                        {formatNumber(week.InternalCalls ?? 0)}
                      </td>
                    </>
                  )}
                  <td className="num text-muted-foreground">
                    {week.Errors > 0 ? formatNumber(week.Errors) : "—"}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UsageMcpWeekTooltip(props: {
  active?: boolean
  payload?: { payload: ChartPoint }[]
  showCustomers: boolean
}) {
  const { active, payload, showCustomers } = props
  const point = payload?.[0]?.payload
  if (!active || !point) return null
  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium">
        {formatIsoWeek(point.week)} · {formatIsoWeekRange(point.week)}
        {point.partial && (
          <span className="text-muted-foreground"> · so far</span>
        )}
      </p>
      <p className="text-muted-foreground">
        {formatNumber(point.calls)} tool calls · {formatNumber(point.users)}{" "}
        {point.users === 1 ? "person" : "people"}
        {showCustomers && (
          <>
            {" "}
            · {formatNumber(point.customers)}{" "}
            {point.customers === 1 ? "customer" : "customers"}
          </>
        )}
      </p>
    </div>
  )
}

export default UsageMcpWeekChart
